/**
 * SharedWorkOrdersPage — Phase 4b.
 *
 * Listet alle Werkstatt-Aufträge auf, die ein anderer Händler an mich
 * weitergeleitet hat (Kapazitäts-Sharing). Status: pending → accept/decline,
 * accepted → angelegte Kopie sichtbar.
 *
 * Route: /dealer/workshop-orders/shared-with-me
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Share2, Check, X as XIcon, Loader2, ArrowLeft, Phone, Mail, Car } from "lucide-react";

import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

const STATUS_BADGE = {
    pending: { label: "Angefragt", color: "bg-amber-100 text-amber-900" },
    accepted: { label: "Angenommen", color: "bg-green-100 text-green-900" },
};

export default function SharedWorkOrdersPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/work-orders/shared-with-me");
            setItems(r.data.items || []);
        } catch (_e) {
            setItems([]);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        if (user === false) navigate("/login");
        if (user && user.role !== "b2b" && user.role !== "admin" && user.role !== "zweiradverkaeufer") navigate("/");
        if (user) load();
    }, [user, navigate, load]);

    const respond = async (wid, accept) => {
        if (!accept && !window.confirm("Anfrage wirklich ablehnen?")) return;
        try {
            const r = await api.post(`/work-orders/${wid}/share-respond`, { accept });
            if (accept && r.data?.shared_copy_id) {
                toast.success(`Auftrag angenommen — Kopie ${r.data.shared_copy_number} erstellt`);
                setTimeout(() => navigate(`/dealer/workshop-orders?focus=${r.data.shared_copy_id}`), 800);
            } else {
                toast.success(accept ? "Auftrag angenommen" : "Abgelehnt");
                load();
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler");
        }
    };

    if (!user) return <div className="p-8">Lade…</div>;

    return (
        <div className="min-h-screen bg-gray-50" data-testid="shared-orders-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
                    <Link to="/dealer/workshop-orders" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="back-to-orders">
                        <ArrowLeft className="w-3 h-3" /> Zurück zu meinen Aufträgen
                    </Link>
                    <div className="mt-2 flex items-center gap-3">
                        <Share2 className="w-7 h-7 text-[#FFCC00]" />
                        <div>
                            <h1 className="font-display font-black text-4xl md:text-5xl">Geteilte Aufträge</h1>
                            <p className="text-sm text-gray-300 mt-1">Andere Händler bieten dir freie Werkstatt-Kapazität an</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-3">
                {loading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>
                ) : items.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-sm p-12 text-center" data-testid="shared-empty">
                        <Share2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                        <p className="font-bold text-gray-700">Keine Anfragen</p>
                        <p className="text-sm text-gray-500 mt-1">Sobald ein anderer Händler einen Auftrag mit dir teilt, erscheint er hier.</p>
                    </div>
                ) : (
                    items.map((o) => {
                        const meta = STATUS_BADGE[o.share_status] || STATUS_BADGE.pending;
                        const veh = o.vehicle || {};
                        return (
                            <div key={o.id} className="bg-white border border-gray-200 rounded-sm p-4" data-testid={`shared-row-${o.id}`}>
                                <div className="flex flex-wrap gap-3 items-start justify-between">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-mono-num font-black text-base">{o.order_number}</span>
                                            <Badge className={`${meta.color} font-bold uppercase tracking-wider text-[10px] rounded-sm`} data-testid={`shared-status-${o.id}`}>
                                                {meta.label}
                                            </Badge>
                                        </div>
                                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                            <div>
                                                <span className="text-[10px] uppercase text-gray-500 block">Anfragender Händler</span>
                                                <span className="font-bold">{o.origin_dealer?.label}</span>
                                                {(o.origin_dealer?.email || o.origin_dealer?.phone) && (
                                                    <div className="text-[11px] text-gray-600 flex flex-wrap gap-2 mt-0.5">
                                                        {o.origin_dealer?.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{o.origin_dealer.email}</span>}
                                                        {o.origin_dealer?.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{o.origin_dealer.phone}</span>}
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <span className="text-[10px] uppercase text-gray-500 block">Fahrzeug</span>
                                                <span className="inline-flex items-center gap-1"><Car className="w-3 h-3" />{[veh.brand, veh.model, veh.license_plate].filter(Boolean).join(" · ") || "—"}</span>
                                            </div>
                                        </div>
                                        {o.shared_note && (
                                            <div className="mt-2 text-[12px] text-gray-700 italic border-l-2 border-amber-400 pl-2 bg-amber-50/40 py-1" data-testid={`shared-note-${o.id}`}>„{o.shared_note}"</div>
                                        )}
                                        {o.customer_complaint && (
                                            <div className="mt-1 text-[12px] text-gray-600 italic">Beanstandung: „{o.customer_complaint}"</div>
                                        )}
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-[10px] uppercase tracking-wider text-gray-500">Brutto</div>
                                        <div className="font-mono-num font-black text-xl">{fmtEur(o.totals?.total_gross)}</div>
                                        <div className="text-[10px] text-gray-500">{(o.positions || []).length} Positionen</div>
                                    </div>
                                </div>
                                {o.share_status === "pending" && (
                                    <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2 justify-end">
                                        <Button onClick={() => respond(o.id, false)} variant="outline" size="sm" className="rounded-sm border-red-300 text-red-700 hover:bg-red-50 text-[11px]" data-testid={`shared-decline-${o.id}`}>
                                            <XIcon className="w-3 h-3 mr-1" /> Ablehnen
                                        </Button>
                                        <Button onClick={() => respond(o.id, true)} size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-sm text-[11px] font-bold uppercase tracking-wider" data-testid={`shared-accept-${o.id}`}>
                                            <Check className="w-3 h-3 mr-1" /> Annehmen — Kopie erstellen
                                        </Button>
                                    </div>
                                )}
                                {o.share_status === "accepted" && o.shared_copy_id && (
                                    <div className="mt-3 pt-3 border-t border-gray-100 flex justify-end">
                                        <Link to={`/dealer/workshop-orders?focus=${o.shared_copy_id}`} className="text-xs font-bold text-blue-700 hover:underline" data-testid={`shared-jump-${o.id}`}>
                                            → Eigene Kopie öffnen
                                        </Link>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
